'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Smartphone, Mail, ChevronRight, AlertCircle, Copy, Check } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

interface LoginFormData {
  identifier: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

type LoginMethod = 'password' | 'otp';

const mockCredentials = [
  { role: 'Enterprise (CXO)', email: 'rajiv.sharma@techcorpglobal.com', password: 'KCF@Enterprise2026' },
  { role: 'Professional', email: 'sarah.chen@techcorpglobal.com', password: 'KCF@Pro2026' },
  { role: 'Individual', email: 'arjun.mehta@kcf.world', password: 'KCF@Individual2026' },
];

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  const [isLoading, setIsLoading] = useState(false);
  const [identifierType, setIdentifierType] = useState<'email' | 'mobile'>('email');
  const [otpSent, setOtpSent] = useState(false);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showSSOModal, setShowSSOModal] = useState(false);

  const { signIn } = useAuth();
  const supabase = createClient();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.identifier, data.password);
      toast.success('Welcome back to KCF Intelligence Platform', {
        description: 'Routing you to your dashboard...',
      });
      router.push('/dashboard');
    } catch (err: any) {
      setError('identifier', {
        message: err?.message || 'Invalid credentials — please check your email and password',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOTP = async () => {
    const identifierEl = document.getElementById('otp-identifier') as HTMLInputElement;
    const email = identifierEl?.value?.trim();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
      setOtpSent(true);
      toast.success('Magic link sent!', { description: 'Check your email inbox' });
    } catch (err: any) {
      toast.error('Failed to send OTP', { description: err?.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newValues = [...otpValues];
    newValues[index] = value.slice(-1);
    setOtpValues(newValues);
    if (value && index < 5) {
      const next = document.getElementById(`otp-input-${index + 1}`);
      if (next) (next as HTMLInputElement).focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      const prev = document.getElementById(`otp-input-${index - 1}`);
      if (prev) (prev as HTMLInputElement).focus();
    }
  };

  const handleCopyCredential = async (text: string, field: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUseCredential = (cred: typeof mockCredentials[0]) => {
    setValue('identifier', cred.email);
    setValue('password', cred.password);
    toast.info('Demo credentials filled', { description: `Signed in as ${cred.role}` });
  };

  const handleSocialLogin = async (provider: 'google' | 'azure') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(`${provider} sign-in failed`, { description: err?.message });
    }
  };

  const handleVerifyOTP = async () => {
    const code = otpValues.join('');
    if (code.length < 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }
    const identifierEl = document.getElementById('otp-identifier') as HTMLInputElement;
    const email = identifierEl?.value?.trim();
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'email',
      });
      if (error) throw error;
      toast.success('OTP verified — Welcome to KCF', { description: 'Routing to your dashboard...' });
      router.push('/dashboard');
    } catch (err: any) {
      toast.error('OTP verification failed', { description: err?.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="space-y-5">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-800 text-foreground mb-1">
            Sign in to your{' '}
            <span className="text-gradient-gold">Intelligence Profile</span>
          </h2>
          <p className="text-sm text-muted-foreground">
            Access your Digital Twin, assessments, and transformation roadmap.
          </p>
        </div>

        {/* Login method toggle */}
        <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--navy-500)' }}>
          <button
            onClick={() => setLoginMethod('password')}
            className="flex-1 py-2 text-xs font-600 tracking-wide transition-all duration-150"
            style={
              loginMethod === 'password'
                ? { background: 'rgba(201,168,76,0.12)', color: 'var(--gold-500)' }
                : { background: 'transparent', color: 'var(--muted-foreground)' }
            }
          >
            Password
          </button>
          <button
            onClick={() => setLoginMethod('otp')}
            className="flex-1 py-2 text-xs font-600 tracking-wide transition-all duration-150"
            style={
              loginMethod === 'otp'
                ? { background: 'rgba(201,168,76,0.12)', color: 'var(--gold-500)' }
                : { background: 'transparent', color: 'var(--muted-foreground)' }
            }
          >
            OTP / Magic Link
          </button>
        </div>

        {/* Identifier type toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIdentifierType('email')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 transition-all"
            style={
              identifierType === 'email'
                ? { background: 'rgba(13,79,108,0.3)', color: '#4DD9FF', border: '1px solid rgba(13,79,108,0.5)' }
                : { background: 'transparent', color: 'var(--muted-foreground)', border: '1px solid transparent' }
            }
          >
            <Mail size={12} />
            Email
          </button>
          <button
            onClick={() => setIdentifierType('mobile')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 transition-all"
            style={
              identifierType === 'mobile'
                ? { background: 'rgba(13,79,108,0.3)', color: '#4DD9FF', border: '1px solid rgba(13,79,108,0.5)' }
                : { background: 'transparent', color: 'var(--muted-foreground)', border: '1px solid transparent' }
            }
          >
            <Smartphone size={12} />
            Mobile
          </button>
        </div>

        {loginMethod === 'password' ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Identifier field */}
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                {identifierType === 'email' ? 'Email Address' : 'Mobile Number'}
              </label>
              <input
                {...register('identifier', {
                  required: `${identifierType === 'email' ? 'Email' : 'Mobile number'} is required`,
                  pattern:
                    identifierType === 'email'
                      ? { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' }
                      : { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit mobile number' },
                })}
                type={identifierType === 'email' ? 'email' : 'tel'}
                placeholder={identifierType === 'email' ? 'you@organisation.com' : '+91 98765 43210'}
                className={`input-field ${errors.identifier ? 'input-error' : ''}`}
                autoComplete={identifierType === 'email' ? 'email' : 'tel'}
              />
              {errors.identifier && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <AlertCircle size={12} className="text-danger flex-shrink-0" />
                  <p className="text-xs text-danger">{errors.identifier.message}</p>
                </div>
              )}
            </div>

            {/* Password field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-600 text-foreground tracking-wide">Password</label>
                <a href="#" className="text-xs text-primary hover:underline">
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  })}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your KCF account password"
                  className={`input-field pr-10 ${errors.password ? 'input-error' : ''}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <AlertCircle size={12} className="text-danger flex-shrink-0" />
                  <p className="text-xs text-danger">{errors.password.message}</p>
                </div>
              )}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                {...register('rememberMe')}
                id="remember-me"
                type="checkbox"
                className="w-4 h-4 rounded accent-primary cursor-pointer"
              />
              <label htmlFor="remember-me" className="text-xs text-muted-foreground cursor-pointer">
                Keep me signed in for 30 days
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary flex items-center justify-center gap-2"
              style={{ minHeight: '44px' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <span>Access Intelligence Platform</span>
                  <ChevronRight size={16} />
                </>
              )}
            </button>
          </form>
        ) : (
          /* OTP Flow */
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1.5 tracking-wide">
                {identifierType === 'email' ? 'Email Address' : 'Mobile Number'}
              </label>
              <div className="flex gap-2">
                <input
                  type={identifierType === 'email' ? 'email' : 'tel'}
                  placeholder={identifierType === 'email' ? 'you@organisation.com' : '+91 98765 43210'}
                  className="input-field flex-1"
                  id="otp-identifier"
                />
                <button
                  onClick={handleSendOTP}
                  disabled={isLoading || otpSent}
                  className="flex-shrink-0 px-4 py-2 rounded-lg text-xs font-700 transition-all"
                  style={{
                    background: otpSent ? 'rgba(16,185,129,0.15)' : 'rgba(201,168,76,0.15)',
                    border: `1px solid ${otpSent ? 'rgba(16,185,129,0.4)' : 'rgba(201,168,76,0.4)'}`,
                    color: otpSent ? 'var(--emerald)' : 'var(--gold-500)',
                  }}
                >
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : otpSent ? 'Sent ✓' : 'Send OTP'}
                </button>
              </div>
            </div>

            {otpSent && (
              <div className="animate-slide-up">
                <label className="block text-xs font-600 text-foreground mb-2 tracking-wide">
                  Enter 6-digit OTP
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  We sent a code to your {identifierType === 'email' ? 'email' : 'mobile'}. Valid for 10 minutes.
                </p>
                <div className="flex items-center gap-2 justify-center mb-4">
                  {otpValues.map((val, idx) => (
                    <input
                      key={`otp-digit-${idx}`}
                      id={`otp-input-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={val}
                      onChange={(e) => handleOTPChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(idx, e)}
                      className="otp-input"
                      aria-label={`OTP digit ${idx + 1}`}
                    />
                  ))}
                </div>
                <button
                  onClick={handleVerifyOTP}
                  disabled={isLoading}
                  className="btn-primary flex items-center justify-center gap-2"
                  style={{ minHeight: '44px' }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify & Access Platform</span>
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
                <button
                  onClick={() => { setOtpSent(false); setOtpValues(['', '', '', '', '', '']); }}
                  className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  Resend OTP
                </button>
              </div>
            )}

            {!otpSent && (
              <p className="text-xs text-muted-foreground text-center">
                A one-time password will be sent to your registered contact.
              </p>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 divider-gold" />
          <span className="text-xs text-muted-foreground">or continue with</span>
          <div className="flex-1 divider-gold" />
        </div>

        {/* Social login */}
        <div className="flex gap-2">
          <button onClick={() => handleSocialLogin('google')} className="btn-social">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Google
          </button>
          <button onClick={() => handleSocialLogin('azure')} className="btn-social">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
              <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
            </svg>
            Microsoft
          </button>
          <button
            onClick={() => setShowSSOModal(true)}
            className="btn-social"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            SSO
          </button>
        </div>

        {/* Create account link */}
        <p className="text-center text-xs text-muted-foreground">
          New to KCF?{' '}
          <button
            onClick={onSwitchToRegister}
            className="text-primary font-600 hover:underline"
          >
            Create your Intelligence Profile
          </button>
        </p>

        {/* Demo credentials box */}
        <DemoCredentialsBox
          credentials={mockCredentials}
          onUse={handleUseCredential}
          onCopy={handleCopyCredential}
          copiedField={copiedField}
        />
      </div>

      {/* Enterprise SSO Modal */}
      {showSSOModal && (
        <SSOModal onClose={() => setShowSSOModal(false)} />
      )}
    </>
  );
}

/* ─────────────────── Demo Credentials Box ─────────────────── */

interface DemoCredential {
  role: string;
  email: string;
  password: string;
}

interface DemoCredentialsBoxProps {
  credentials: DemoCredential[];
  onUse: (cred: DemoCredential) => void;
  onCopy: (text: string, field: string) => void;
  copiedField: string | null;
}

function DemoCredentialsBox({ credentials, onUse, onCopy, copiedField }: DemoCredentialsBoxProps) {
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: 'rgba(13,79,108,0.08)',
        border: '1px solid rgba(13,79,108,0.25)',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-5 h-5 rounded flex items-center justify-center"
          style={{ background: 'rgba(13,79,108,0.3)' }}
        >
          <AlertCircle size={11} style={{ color: '#4DD9FF' }} />
        </div>
        <p className="text-xs font-700 tracking-wide" style={{ color: '#4DD9FF' }}>
          Demo Credentials — Click to auto-fill
        </p>
      </div>
      <div className="space-y-2">
        {credentials.map((cred, idx) => (
          <div
            key={`demo-cred-${idx}`}
            className="flex items-center justify-between rounded-lg px-3 py-2 cursor-pointer transition-all hover:bg-opacity-80"
            style={{ background: 'rgba(15,30,53,0.6)', border: '1px solid rgba(30,58,90,0.5)' }}
            onClick={() => onUse(cred)}
          >
            <div className="flex-1 min-w-0">
              <p className="text-2xs font-700 text-primary mb-0.5">{cred.role}</p>
              <p className="text-2xs text-muted-foreground truncate font-mono">{cred.email}</p>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={(e) => { e.stopPropagation(); onCopy(cred.email, `email-${idx}`); }}
                className="p-1 rounded hover:bg-navy-600 transition-colors"
                aria-label="Copy email"
              >
                {copiedField === `email-${idx}` ? (
                  <Check size={11} className="text-emerald" />
                ) : (
                  <Copy size={11} className="text-muted-foreground" />
                )}
              </button>
              <span
                className="text-2xs font-700 px-2 py-0.5 rounded"
                style={{ background: 'rgba(201,168,76,0.1)', color: 'var(--gold-500)' }}
              >
                Use
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── Enterprise SSO Modal ─────────────────── */

interface SSOModalProps {
  onClose: () => void;
}

function SSOModal({ onClose }: SSOModalProps) {
  const [domain, setDomain] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const handleCheckSSO = async () => {
    if (!domain) return;
    setIsChecking(true);
    // BACKEND INTEGRATION: POST /api/auth/sso/lookup — check if domain has SSO configured
    await new Promise((res) => setTimeout(res, 1500));
    setIsChecking(false);
    toast.info(`SSO not configured for ${domain}`, {
      description: 'Contact your IT administrator or use standard login.',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-navy-900 opacity-80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-sm rounded-xl p-6 animate-slide-up"
        style={{ background: 'var(--navy-700)', border: '1px solid var(--navy-500)', boxShadow: '0 24px 60px rgba(5,13,26,0.8)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-700 text-foreground">Enterprise SSO</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-navy-600 transition-all"
          >
            ✕
          </button>
        </div>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Enter your organisation&apos;s email domain to detect your SSO provider (Okta, Azure AD, SAML, etc.)
        </p>
        <div className="mb-4">
          <label className="block text-xs font-600 text-foreground mb-1.5">Organisation Domain</label>
          <input
            type="text"
            placeholder="techcorpglobal.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="input-field"
          />
          <p className="text-2xs text-muted-foreground mt-1">
            Enter just the domain, not the full email address.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" style={{ width: 'auto' }}>
            Cancel
          </button>
          <button
            onClick={handleCheckSSO}
            disabled={!domain || isChecking}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
            style={{ width: 'auto', minHeight: '40px' }}
          >
            {isChecking ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              'Detect SSO'
            )}
          </button>
        </div>
        <p className="text-2xs text-muted-foreground mt-3 text-center">
          Institution SSO? Contact{' '}
          <a href="mailto:sso@knowledgecapital.world" className="text-primary hover:underline">
            sso@knowledgecapital.world
          </a>
        </p>
      </div>
    </div>
  );
}